// AssignTrainingPage.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { Modal, Button } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";

const parseJsonField = (raw, fallback = []) => {
  try {
    if (raw == null) return fallback;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "number") return [raw];
    if (typeof raw === "string") {
      const s = raw.trim();
      if (!s) return fallback;
      if (s.startsWith("[") && s.endsWith("]")) return JSON.parse(s);
      const n = Number(s);
      return Number.isNaN(n) ? [s] : [n];
    }
    if (typeof raw === "object") {
      const vals = Array.isArray(raw) ? raw : Object.values(raw);
      return Array.isArray(vals) ? vals : fallback;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

const AssignTrainingPage = ({ userId, onClose }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // Data
  const [programs, setPrograms] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedExternalModules, setSelectedExternalModules] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);

  // Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingModuleIds, setEditingModuleIds] = useState([]);
  const [editingCategoryIds, setEditingCategoryIds] = useState([]);
  const [modalModules, setModalModules] = useState([]);
  const [positionCategories, setPositionCategories] = useState([]);

  // search + paging for modules
  const [modSearch, setModSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    trainings: {
      assign: hasPermission("Training", "Training", "assign"),
      edit: hasPermission("Training", "Training", "edit"),
    },
  };

  useEffect(() => {
    const load = async () => {
      const [progRes, modRes, userRes, catRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/trainings/programs`, { headers }),
        axios.get(`${BASE_URL}/api/trainings/modules/all`, { headers }),
        axios.get(`${BASE_URL}/api/users/${userId}`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
      ]);

      setPrograms(progRes.data || []);
      setModules(modRes.data || []);

      // ✅ map categories same way as TrainingQuiz
      setPositionCategories(
        (catRes.data || []).map((c) => ({
          label: c.c_name || c.c_translatedText || c.name,
          value: c.c_id,
        })),
      );

      const user = userRes.data || {};
      const programIds = parseJsonField(user.training_programs, []);
      const moduleIds = parseJsonField(user.training_ids, []);
      const extModuleIds = parseJsonField(user.ext_training_ids, []);

      setSelectedPrograms(programIds.map(Number).filter(Number.isFinite));
      setSelectedModules(moduleIds.map(Number).filter(Number.isFinite));
      setSelectedExternalModules(
        extModuleIds.map(Number).filter(Number.isFinite),
      );
    };

    load().catch((e) => console.error("AssignTraining load failed:", e));
  }, [BASE_URL, headers, userId]);

  // toggle program selection
  const toggleProgram = (progId) => {
    const prog = programs.find((p) => p.id === progId);
    if (!prog) return;

    if (selectedPrograms.includes(progId)) {
      setSelectedPrograms((prev) => prev.filter((id) => id !== progId));
      setSelectedModules((prev) =>
        prev.filter((id) => !prog.moduleIds.includes(id)),
      );
    } else {
      setSelectedPrograms((prev) => [...prev, progId]);
      setSelectedModules((prev) =>
        Array.from(new Set([...prev, ...prog.moduleIds])),
      );
    }
  };

  const toggleModule = (modId) =>
    setSelectedModules((prev) =>
      prev.includes(modId)
        ? prev.filter((id) => id !== modId)
        : [...prev, modId],
    );

  const handleSave = async () => {
    await axios.post(
      `${BASE_URL}/api/trainings/assign`,
      {
        userId,
        trainingIds: selectedModules,
        extTrainingIds: selectedExternalModules,
        programIds: selectedPrograms,
      },
      { headers },
    );

    alert("✔️ Training assignments saved.");
  };

  const handleSaveWithLoading = async () => {
    if (isSavingAssignments) return;

    try {
      setIsSavingAssignments(true);
      await handleSave();
    } catch (err) {
      console.error("Failed to save training assignments", err);
      alert("Failed to save training assignments. Please try again.");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  // Edit modal functions
  const openEditModal = (program) => {
    setEditingProgram(program);
    setEditingModuleIds(program.moduleIds);
    setEditingCategoryIds(program.rawCategory || []);
    setModalModules(modules); // reuse already-loaded modules
    setShowModal(true);
  };

  const saveEditedProgram = async () => {
    if (!editingProgram) return;
    try {
      await axios.put(
        `${BASE_URL}/api/trainings/programs/${editingProgram.id}`,
        {
          name: editingProgram.name,
          moduleIds: editingModuleIds,
          category: editingCategoryIds,
          active: 1,
        },
        { headers },
      );

      const updatedCatNames = positionCategories
        .filter((c) => editingCategoryIds.includes(c.value))
        .map((c) => c.label);

      setPrograms((prev) =>
        prev.map((p) =>
          p.id === editingProgram.id
            ? {
                ...p,
                moduleIds: editingModuleIds,
                category: updatedCatNames,
                rawCategory: editingCategoryIds,
              }
            : p,
        ),
      );

      setShowModal(false);
      alert("✔️ Program updated successfully!");
    } catch (err) {
      console.error("❌ Failed to save edited program", err);
      alert("⚠️ Save failed. Please retry.");
    }
  };

  // filtering & paging
  const filteredModules = useMemo(() => {
    const q = (modSearch || "").toLowerCase().trim();
    if (!q) return modules;
    return modules.filter((m) => {
      const t = String(m.tm_title || "").toLowerCase();
      const c = String(m.tm_category || "").toLowerCase();
      const l = String(m.tm_lang || "").toLowerCase();
      return t.includes(q) || c.includes(q) || l.includes(q);
    });
  }, [modules, modSearch]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredModules.slice(start, start + perPage);
  }, [filteredModules, page]);

  useEffect(() => setPage(1), [modSearch]);
  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0 text-center flex-grow-1">Assign Trainings</h3>
      </div>

      {/* Programs */}
      <h5 className="mt-3">Program Assignment List</h5>
      <div className="table-responsive mb-4">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Program Name</th>
              <th>Category</th>
              <th>Edit Program Modules</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {programs.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No programs found.
                </td>
              </tr>
            ) : (
              programs.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    {Array.isArray(p.category) ? p.category.join(", ") : ""}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEditModal(p)}
                      disabled={!PERMISSIONS.trainings.edit}
                    >
                      ✏️
                    </button>
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedPrograms.includes(p.id)}
                      onChange={() => toggleProgram(p.id)}
                      disabled={!PERMISSIONS.trainings.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modules */}
      <div className="d-flex justify-content-between align-items-end mb-2">
        <h5 className="m-0">Module Assignment List</h5>
        <div className="nw-filter-bar" style={{ minWidth: 280 }}>
          <input
            className="form-control"
            placeholder="Search"
            value={modSearch}
            onChange={(e) => setModSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Module Name</th>
              <th>Category</th>
              <th>Language</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No modules match your search.
                </td>
              </tr>
            ) : (
              paged.map((m) => (
                <tr key={m.tm_id}>
                  <td>{m.tm_title}</td>
                  <td>{m.tm_category || "Learning Centre"}</td>
                  <td>{m.tm_lang || "ENG"}</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedModules.includes(m.tm_id)}
                      onChange={() => toggleModule(m.tm_id)}
                      disabled={!PERMISSIONS.trainings.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          totalItems={filteredModules.length}
          itemsPerPage={perPage}
          onPageChange={setPage}
        />
      </div>

      <div className="d-flex justify-content-end pt-2">
        {PERMISSIONS.trainings.assign && (
          <button
            className="btn btn-primary ms-3"
            onClick={handleSaveWithLoading}
            disabled={isSavingAssignments}
          >
            {isSavingAssignments ? "Saving..." : "Save Assignments"}
          </button>
        )}
      </div>

      {/* Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Included Modules</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProgram && (
            <>
              <p className="text-muted">
                Editing: <strong>{editingProgram.name}</strong>
              </p>
              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Employee Categories
                </label>
                <Select
                  isMulti
                  options={positionCategories}
                  value={positionCategories.filter((c) =>
                    editingCategoryIds.includes(c.value),
                  )}
                  onChange={(selected) =>
                    setEditingCategoryIds(selected.map((s) => s.value))
                  }
                  placeholder="Search and select categories..."
                />
              </div>
              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Modules to Include
                </label>
                <Select
                  isMulti
                  value={modalModules
                    .filter((m) => editingModuleIds.includes(m.tm_id))
                    .map((m) => ({ value: m.tm_id, label: m.tm_title }))}
                  onChange={(selected) =>
                    setEditingModuleIds(selected.map((s) => s.value))
                  }
                  options={modalModules.map((m) => ({
                    value: m.tm_id,
                    label: m.tm_title,
                  }))}
                  placeholder="Search and select modules..."
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditedProgram}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignTrainingPage;
